import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Plus,
  Calendar,
  MessageCircle,
  FileText,
  Users,
  Settings as SettingsIcon,
  Clock,
  ChevronRight,
  Sparkles,
} from "lucide-react";

const QuickActions = () => {
  const reduceMotion = useReducedMotion();

  const actions = [
    {
      id: 1,
      title: "Create New",
      description: "Start a new message or campaign",
      icon: Plus,
      color: "from-indigo-600 to-violet-600",
      bgColor: "bg-gradient-to-br from-indigo-50 to-violet-100",
      borderColor: "border-indigo-200",
      link: "/create-new",
      badge: "Most Used",
      stats: "24 this week",
      delay: 0.1,
    },
    {
      id: 2,
      title: "Schedule Messages",
      description: "Plan and schedule your messages",
      icon: Calendar,
      color: "from-violet-600 to-fuchsia-600",
      bgColor: "bg-gradient-to-br from-violet-50 to-fuchsia-100",
      borderColor: "border-violet-200",
      link: "/schedule",
      badge: "Auto-run",
      stats: "5 pending",
      delay: 0.15,
    },
    {
      id: 3,
      title: "Birthday Messages",
      description: "Set up automated birthday wishes",
      icon: MessageCircle,
      color: "from-rose-600 to-pink-600",
      bgColor: "bg-gradient-to-br from-rose-50 to-pink-100",
      borderColor: "border-rose-200",
      link: "/Birthday/schedule",
      badge: "Auto",
      stats: "12 today",
      delay: 0.2,
    },
    {
      id: 4,
      title: "Create Template",
      description: "Design and manage message templates",
      icon: FileText,
      color: "from-cyan-600 to-sky-600",
      bgColor: "bg-gradient-to-br from-cyan-50 to-sky-100",
      borderColor: "border-cyan-200",
      link: "/template",
      badge: "5 new",
      stats: "12 total",
      delay: 0.25,
    },
    {
      id: 5,
      title: "Manage Groups",
      description: "Organize WhatsApp groups & contacts",
      icon: Users,
      color: "from-emerald-600 to-teal-600",
      bgColor: "bg-gradient-to-br from-emerald-50 to-teal-100",
      borderColor: "border-emerald-200",
      link: "/whatsapp-groups-details",
      badge: "Active",
      stats: "3 groups",
      delay: 0.3,
    },
    {
      id: 6,
      title: "Settings",
      description: "Configure account & preferences",
      icon: SettingsIcon,
      color: "from-indigo-500 to-violet-500",
      bgColor: "bg-gradient-to-br from-indigo-50 to-violet-100",
      borderColor: "border-indigo-200",
      link: "/settings",
      badge: "Updated",
      stats: "2 new",
      delay: 0.35,
    },
  ];

  return (
    <motion.div
      className="glass-surface rounded-2xl border border-indigo-200/70 p-4"
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-gradient-to-br from-indigo-100 to-violet-100 p-1.5">
            <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Quick Actions</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Speed up your workflow with one-click access
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <motion.div
              key={action.id}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                duration: 0.3,
                delay: action.delay,
                type: "spring",
                stiffness: 100,
              }}
              whileHover={
                reduceMotion
                  ? undefined
                  : { scale: 1.02, y: -4, transition: { duration: 0.2 } }
              }
              whileTap={reduceMotion ? undefined : { scale: 0.98 }}
            >
              <Link
                to={action.link}
                className={`group relative block overflow-hidden rounded-xl border p-3.5 transition-all duration-300 hover:shadow-lg ${action.bgColor} ${action.borderColor}`}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${action.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
                />

                <div className="relative flex items-start justify-between mb-3">
                  <div
                    className={`p-2 rounded-lg bg-gradient-to-br ${action.color} shadow-md`}
                  >
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex items-center gap-2">
                    {action.badge && (
                      <span className="px-1.5 py-0.5 rounded-full bg-white text-[10px] font-medium text-gray-700 shadow-sm">
                        {action.badge}
                      </span>
                    )}
                    <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>

                {/* Content */}
                <div className="relative">
                  <h4 className="font-semibold text-gray-900 text-xs mb-0.5">
                    {action.title}
                  </h4>
                  <p className="text-[11px] text-gray-600 mb-2">
                    {action.description}
                  </p>

                  {action.stats && (
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className="text-[11px] text-gray-500">
                          {action.stats}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-400 group-hover:text-gray-600 transition-colors">
                        Click to open →
                      </div>
                    </div>
                  )}
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-current to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
              </Link>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default QuickActions;
